import React, { useMemo, useState } from 'react';
import { useQuery } from 'urql';
import styled from '@emotion/styled';
import DefaultAccordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import InfoIcon from '@material-ui/icons/Info';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { AggregationType, Change, DataType } from '../../common/types';
import { useClients } from '../../clients/useClients';
import { useHeader } from '../header/useHeader';
import {
  getAsins,
  GetAsinsResponse,
  GetDataPointsAggregateResponse,
  getDataPointsAsinGrouped,
  getDataPointsSubcategoryGrouped,
  getFilteredDataPoints,
  getFilteredDataPointsWaterfallGraph,
} from '../../api/data';
import LoadingIndicator from '../LoadingIndicator';
import {
  transformDataPointAggregateSecondary,
  transformOrderedRevenueTopFiveFilterData,
} from '../dashboard/transformers';
import { withAccordionOverrides, withSecondaryBreakdownStyles } from './styles';
import CustomTooltip from '../Tooltip';
import { prettyPrintDate } from '../graphs/waterfall/utils';
import FilterList from './FilterList';
import FullScreenModal from '../fullScreenModal/FullScreenModal';
import { SecondaryBreakdownDefinition } from './SecondaryBreakdownDefinitions';
import { dateFormat } from '../header/utils';
import { prefixPositiveOrNegative } from '../../common/utils';

const Breakdown = styled.div`
  ${withSecondaryBreakdownStyles()}
`;

const Accordion = styled(DefaultAccordion)`
  ${withAccordionOverrides(true)}
`;

const IconBtn = styled(IconButton)`
  position: absolute !important;
  top: 8px !important;
  right: 4px !important;
  color: #000 !important;
  &::hover {
    color: #000 !important;
  }
`;

export interface SecondaryBreakdownData {
  delta: {
    change: Change;
    percentage: string;
  };
  error?: boolean;
  processing: boolean;
  value: string;
}

export interface Props {
  fieldKey: string;
  link: string;
  title: string;
  valueType: DataType;
  aggregationType?: AggregationType;
}

const SecondaryBreakdown: React.FC<Props> = ({
  fieldKey,
  title,
  valueType,
  aggregationType,
}) => {
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [showDefinitionModal, setShowDefinitionModal] = useState(false);

  const { currentClient } = useClients();

  const {
    allFilters,
    selectedFilters,
    selectedDates,
    previousPeriodDates,
    isReady,
  } = useHeader();

  const pauseQueries: boolean = useMemo(
    () =>
      currentClient.id === '' ||
      selectedDates.startDate === null ||
      selectedDates.endDate === null,
    [currentClient, selectedDates],
  );

  const commonQueryVariables = {
    client_id: currentClient.id,
    fieldConfiguration_fieldKey: fieldKey,
    asin_id_list: selectedFilters
      .filter(filter => filter.type === 'asin')
      .map(filter => filter.id),
    asin_category_list: selectedFilters
      .filter(filter => filter.type === 'category')
      .map(filter => filter.id),
    asin_subcategory_list: selectedFilters
      .filter(filter => filter.type === 'subcategory')
      .map(filter => filter.id),
    aggregateOperation: aggregationType,
  };

  const [getTopFiveCategoryDataResponse] = useQuery<
    GetDataPointsAggregateResponse
  >({
    query: getFilteredDataPointsWaterfallGraph,
    variables: {
      ...commonQueryVariables,
      startDate:
        selectedDates.startDate === null
          ? null
          : selectedDates.startDate.format(dateFormat),
      endDate:
        selectedDates.endDate === null
          ? null
          : selectedDates.endDate.format(dateFormat),
      order: 'desc',
    },
    pause: pauseQueries || (selectedFilters.length !== 0 && !allFilters),
  });

  const [getTopFivePreviousCategoryDataResponse] = useQuery<
    GetDataPointsAggregateResponse
  >({
    query: getFilteredDataPointsWaterfallGraph,
    variables: {
      ...commonQueryVariables,
      startDate:
        previousPeriodDates.startDate === null
          ? null
          : previousPeriodDates.startDate.format(dateFormat),
      endDate:
        previousPeriodDates.endDate === null
          ? null
          : previousPeriodDates.endDate.format(dateFormat),
      order: 'desc',
      asin_id_list: null,
      asin_subcategory_list: null,
      asin_category_list:
        getTopFiveCategoryDataResponse.data &&
        getTopFiveCategoryDataResponse.data.dataPointAggregates &&
        getTopFiveCategoryDataResponse.data.dataPointAggregates.edges
          ? getTopFiveCategoryDataResponse.data.dataPointAggregates.edges
              .slice(0, 5)
              .map(edge => edge.node.categoryId)
          : [],
    },
    pause:
      pauseQueries ||
      (selectedFilters.length !== 0 && !allFilters) ||
      getTopFiveCategoryDataResponse.fetching,
  });

  const [getTopFiveSubCategoryDataResponse] = useQuery<
    GetDataPointsAggregateResponse
  >({
    query: getDataPointsSubcategoryGrouped,
    variables: {
      ...commonQueryVariables,
      startDate:
        selectedDates.startDate === null
          ? null
          : selectedDates.startDate.format(dateFormat),
      endDate:
        selectedDates.endDate === null
          ? null
          : selectedDates.endDate.format(dateFormat),
      order: 'desc',
      asin_id_list: null,
      asin_subcategory_list: null,
      aggregateOperation: null,
    },
    pause:
      pauseQueries ||
      allFilters ||
      selectedFilters.length === 0 ||
      selectedFilters[0].type !== 'category',
  });

  const [getTopFivePreviousSubCategoryDataResponse] = useQuery<
    GetDataPointsAggregateResponse
  >({
    query: getDataPointsSubcategoryGrouped,
    variables: {
      ...commonQueryVariables,
      startDate:
        previousPeriodDates.startDate === null
          ? null
          : previousPeriodDates.startDate.format(dateFormat),
      endDate:
        previousPeriodDates.endDate === null
          ? null
          : previousPeriodDates.endDate.format(dateFormat),
      order: 'desc',
      asin_subcategory_list:
        getTopFiveSubCategoryDataResponse.data &&
        getTopFiveSubCategoryDataResponse.data.dataPointAggregates &&
        getTopFiveSubCategoryDataResponse.data.dataPointAggregates.edges
          ? getTopFiveSubCategoryDataResponse.data.dataPointAggregates.edges
              .slice(0, 5)
              .map(edge => edge.node.subcategoryId)
          : [],
      asin_id_list: null,
      asin_category_list: null,
      aggregateOperation: null,
    },
    pause:
      pauseQueries ||
      allFilters ||
      selectedFilters.length === 0 ||
      selectedFilters[0].type !== 'category' ||
      getTopFiveSubCategoryDataResponse.fetching ||
      (getTopFiveSubCategoryDataResponse.data &&
        getTopFiveSubCategoryDataResponse.data.dataPointAggregates &&
        getTopFiveSubCategoryDataResponse.data.dataPointAggregates.edges &&
        getTopFiveSubCategoryDataResponse.data.dataPointAggregates.edges
          .length === 0),
  });

  const [getTopFiveAsinsDataResponse] = useQuery<
    GetDataPointsAggregateResponse
  >({
    query: getDataPointsAsinGrouped,
    variables: {
      ...commonQueryVariables,
      startDate:
        selectedDates.startDate === null
          ? null
          : selectedDates.startDate.format(dateFormat),
      endDate:
        selectedDates.endDate === null
          ? null
          : selectedDates.endDate.format(dateFormat),
      order: 'desc',
      aggregateOperation: null,
    },
    pause:
      pauseQueries ||
      selectedFilters.length === 0 ||
      !['subcategory', 'asin'].includes(selectedFilters[0].type),
  });

  const [getTopFiveAsinsPreviousDataResponse] = useQuery<
    GetDataPointsAggregateResponse
  >({
    query: getDataPointsAsinGrouped,
    variables: {
      ...commonQueryVariables,
      startDate:
        previousPeriodDates.startDate === null
          ? null
          : previousPeriodDates.startDate.format(dateFormat),
      endDate:
        previousPeriodDates.endDate === null
          ? null
          : previousPeriodDates.endDate.format(dateFormat),
      asin_id_list:
        getTopFiveAsinsDataResponse.data &&
        getTopFiveAsinsDataResponse.data.dataPointAggregates &&
        getTopFiveAsinsDataResponse.data.dataPointAggregates.edges
          ? getTopFiveAsinsDataResponse.data.dataPointAggregates.edges
              .slice(0, 5)
              .map(edge => edge.node.asinId)
          : [],
      asin_category_list: null,
      asin_subcategory_list: null,
      aggregateOperation: null,
    },
    pause: pauseQueries || getTopFiveAsinsDataResponse.fetching,
  });

  const [getAsinsResponse] = useQuery<GetAsinsResponse>({
    query: getAsins,
    variables: {
      asin_id_list:
        getTopFiveAsinsDataResponse.data &&
        getTopFiveAsinsDataResponse.data.dataPointAggregates &&
        getTopFiveAsinsDataResponse.data.dataPointAggregates.edges
          ? getTopFiveAsinsDataResponse.data.dataPointAggregates.edges
              .slice(0, 5)
              .map(edge => edge.node.asinId)
          : [],
    },
    pause:
      pauseQueries ||
      getTopFiveAsinsDataResponse.fetching ||
      (getTopFiveAsinsDataResponse.data &&
        getTopFiveAsinsDataResponse.data.dataPointAggregates &&
        getTopFiveAsinsDataResponse.data.dataPointAggregates.edges &&
        getTopFiveAsinsDataResponse.data.dataPointAggregates.edges.length ===
          0),
  });

  const [getDataPointsAggregateResponse] = useQuery<
    GetDataPointsAggregateResponse
  >({
    query: getFilteredDataPoints,
    variables: {
      startDate:
        selectedDates.startDate === null
          ? null
          : selectedDates.startDate.format(dateFormat),
      endDate:
        selectedDates.endDate === null
          ? null
          : selectedDates.endDate.format(dateFormat),
      ...commonQueryVariables,
    },
    pause: pauseQueries,
  });

  const [getDataPointsAggregatePreviousPeriodResponse] = useQuery<
    GetDataPointsAggregateResponse
  >({
    query: getFilteredDataPoints,
    variables: {
      startDate:
        previousPeriodDates.startDate === null
          ? null
          : previousPeriodDates.startDate.format(dateFormat),
      endDate:
        previousPeriodDates.endDate === null
          ? null
          : previousPeriodDates.endDate.format(dateFormat),
      ...commonQueryVariables,
    },
    pause: pauseQueries,
  });

  const data = transformDataPointAggregateSecondary(
    getDataPointsAggregateResponse,
    getDataPointsAggregatePreviousPeriodResponse,
    valueType,
  );

  const deltaTitleTooltipText = (
    <div style={{ textAlign: 'center' }}>
      Change from <br />
      {prettyPrintDate(previousPeriodDates.startDate)} to{' '}
      {prettyPrintDate(previousPeriodDates.endDate)}
    </div>
  );

  const handleMoreVertButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleViewDefinitionClick = () => {
    setShowDefinitionModal(true);
    handleMenuClose();
  };
  const singleAsinSelected = () =>
    selectedFilters?.length === 1 && selectedFilters[0]?.type === 'asin';

  const ORTop5Data = transformOrderedRevenueTopFiveFilterData(
    selectedFilters.length === 0 || allFilters
      ? getTopFiveCategoryDataResponse
      : selectedFilters[0].type === 'category'
      ? getTopFiveSubCategoryDataResponse
      : getTopFiveAsinsDataResponse,
    selectedFilters.length === 0 || allFilters
      ? getTopFivePreviousCategoryDataResponse
      : selectedFilters[0].type === 'category'
      ? getTopFivePreviousSubCategoryDataResponse
      : getTopFiveAsinsPreviousDataResponse,
    selectedFilters.length === 0 || allFilters
      ? 'category'
      : selectedFilters[0].type === 'category'
      ? 'subcategory'
      : 'asin',
    valueType,
    getAsinsResponse,
  );
  const loading = () => {
    return (
      !isReady ||
      data.processing ||
      getDataPointsAggregateResponse.fetching ||
      ORTop5Data.loading ||
      getDataPointsAggregatePreviousPeriodResponse.fetching
    );
  };

  return (
    <Breakdown>
      {loading() ? (
        <LoadingIndicator />
      ) : (
        <>
          <div className="title">{title}</div>
          <div className="data">
            {data.error ? (
              'No data available'
            ) : (
              <>
                <IconBtn
                  className="more-vert-icon"
                  aria-controls="more-vert-menu"
                  aria-haspopup="true"
                  onClick={handleMoreVertButtonClick}
                >
                  <MoreVertIcon color="inherit" />
                </IconBtn>
                <Menu
                  id="more-vert-menu"
                  anchorEl={menuAnchorEl}
                  keepMounted
                  open={Boolean(menuAnchorEl)}
                  onClose={handleMenuClose}
                >
                  <MenuItem onClick={() => handleViewDefinitionClick()}>
                    Show Definition
                  </MenuItem>
                </Menu>
                <div className="total">{data.value}</div>
                <div className="delta">
                  <div className="delta_data">
                    <span className="delta_title">Change</span>
                    <CustomTooltip title={deltaTitleTooltipText} arrow>
                      <InfoIcon className="info-icon" />
                    </CustomTooltip>
                    <span className={`${data.delta.change}_delta`}>
                      {prefixPositiveOrNegative(data.delta.change)}
                      {data.delta.percentage}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
          {(!data.error &&
            !singleAsinSelected() &&
            data.value !== '--' &&
            ORTop5Data.data.length && (
              <Accordion
                elevation={0}
                expanded={accordionExpanded}
                onChange={() => setAccordionExpanded(!accordionExpanded)}
              >
                <AccordionSummary
                  className="breakdown_summary_container"
                  expandIcon={<ExpandMoreIcon />}
                >
                  <span className="breakdown_summary">
                    Top 5{' '}
                    {selectedFilters.length === 0 || allFilters
                      ? 'Categories'
                      : selectedFilters[0].type === 'category'
                      ? 'Subcategories'
                      : 'ASINs'}
                  </span>
                  <span className="breakdown_summary accordian-button">
                    {accordionExpanded ? 'Collapse' : 'Expand'}
                  </span>
                </AccordionSummary>
                <AccordionDetails>
                  <FilterList {...ORTop5Data} />
                </AccordionDetails>
              </Accordion>
            )) || (
            <Accordion elevation={0}>
              <AccordionSummary className="breakdown_summary_container">
                <span className="breakdown_summary"></span>
              </AccordionSummary>
            </Accordion>
          )}
          <FullScreenModal
            open={showDefinitionModal}
            onClose={() => setShowDefinitionModal(false)}
          >
            <SecondaryBreakdownDefinition fieldKey={fieldKey} />
          </FullScreenModal>
        </>
      )}
    </Breakdown>
  );
};

export default SecondaryBreakdown;
